// Copyright (c) 2025-2026 Motswagae Holding (Pty) Ltd. All Rights Reserved.
// CIPC Registration: 2019/443098/07
// Author: Velly Sipho Phaloane
//
// This source code is the proprietary and confidential property of
// Motswagae Holding (Pty) Ltd, trading as 360Wallet.
// Unauthorized copying, modification, distribution, or use of this
// software, in whole or in part, is strictly prohibited.
//
// Protected under the Copyright Act 98 of 1978 (South Africa)
// and international copyright treaties.
import { useState, useEffect, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { Switch } from "../components/ui/switch";
import { toast } from "sonner";
import { Plus, Store, Edit2, Trash2, Mail, DollarSign, Shield, Clock, Search } from "lucide-react";
import axios from "axios";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const RetailStores = () => {
  const [stores, setStores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingStore, setEditingStore] = useState(null);
  const [formData, setFormData] = useState({
    issuer_code: "",
    display_name: "",
    description: "",
    voucher_prefixes: "",
    pin_min_length: 4,
    pin_max_length: 6,
    voucher_expiry_hours: 48,
    partial_withdrawals_allowed: false,
    authentication_method: "HMAC",
    uat_base_url: "",
    production_base_url: "",
    billing_address: "",
    billing_email_to: "",
    billing_email_cc: "",
    billing_email_bcc: ""
  });

  const fetchStores = useCallback(async () => {
    try {
      const token = localStorage.getItem("360wallet_token");
      if (!token) {
        toast.error("Please login again");
        return;
      }
      const response = await axios.get(`${API_URL}/api/retail-stores`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStores(response.data);
    } catch (error) {
      if (error.response?.status === 401) {
        toast.error("Session expired. Please login again.");
      } else {
        toast.error("Failed to load retail stores");
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStores();
  }, [fetchStores]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem("360wallet_token");
    
    const payload = {
      ...formData,
      voucher_prefixes: formData.voucher_prefixes ? formData.voucher_prefixes.split(",").map(p => p.trim().toUpperCase()).filter(Boolean) : [],
      billing_email_to: formData.billing_email_to ? formData.billing_email_to.split(",").map(e => e.trim()).filter(Boolean) : [],
      billing_email_cc: formData.billing_email_cc ? formData.billing_email_cc.split(",").map(e => e.trim()).filter(Boolean) : [],
      billing_email_bcc: formData.billing_email_bcc ? formData.billing_email_bcc.split(",").map(e => e.trim()).filter(Boolean) : []
    };

    try {
      if (editingStore) {
        await axios.put(`${API_URL}/api/retail-stores/${editingStore.issuer_code}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Retail store updated successfully");
      } else {
        await axios.post(`${API_URL}/api/retail-stores`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Retail store created successfully");
      }
      setIsDialogOpen(false);
      resetForm();
      fetchStores();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save retail store");
    }
  };

  const handleEdit = (store) => {
    setEditingStore(store);
    setFormData({
      issuer_code: store.issuer_code,
      display_name: store.display_name,
      description: store.description || "",
      voucher_prefixes: (store.voucher_prefixes || []).join(", "),
      pin_min_length: store.pin_min_length || 4,
      pin_max_length: store.pin_max_length || 6,
      voucher_expiry_hours: store.voucher_expiry_hours || 48,
      partial_withdrawals_allowed: store.partial_withdrawals_allowed || false,
      authentication_method: store.authentication_method || "HMAC",
      uat_base_url: store.uat_base_url || "",
      production_base_url: store.production_base_url || "",
      billing_address: store.billing_address || "",
      billing_email_to: (store.billing_email_to || []).join(", "),
      billing_email_cc: (store.billing_email_cc || []).join(", "),
      billing_email_bcc: (store.billing_email_bcc || []).join(", ")
    });
    setIsDialogOpen(true);
  };

  const handleToggleStatus = async (store) => {
    try {
      const token = localStorage.getItem("360wallet_token");
      await axios.patch(`${API_URL}/api/retail-stores/${store.issuer_code}/status`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Store ${store.status === "ACTIVE" ? "suspended" : "activated"}`);
      fetchStores();
    } catch (error) {
      toast.error("Failed to update store status");
    }
  };

  const handleDelete = async (issuerCode) => {
    if (!window.confirm("Are you sure you want to delete this retail store?")) return;
    
    try {
      const token = localStorage.getItem("360wallet_token");
      await axios.delete(`${API_URL}/api/retail-stores/${issuerCode}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Retail store deleted");
      fetchStores();
    } catch (error) {
      toast.error("Failed to delete retail store");
    }
  };

  const resetForm = () => {
    setFormData({
      issuer_code: "",
      display_name: "",
      description: "",
      voucher_prefixes: "",
      pin_min_length: 4,
      pin_max_length: 6,
      voucher_expiry_hours: 48,
      partial_withdrawals_allowed: false,
      authentication_method: "HMAC",
      uat_base_url: "",
      production_base_url: "",
      billing_address: "",
      billing_email_to: "",
      billing_email_cc: "",
      billing_email_bcc: ""
    });
    setEditingStore(null);
  };

  const filteredStores = stores.filter(store =>
    store.display_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    store.issuer_code.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const formatCurrency = (amount) => `R${amount.toFixed(2)}`;

  const StatusBadge = ({ status }) => {
    const isActive = status === "ACTIVE";
    return (
      <span className={`px-2 py-0.5 rounded text-xs ${isActive ? "bg-green-500/20 text-green-400" : "bg-red-500/20 text-red-400"}`}>
        {status}
      </span>
    );
  };

  const AuthMethodBadge = ({ method }) => {
    const colors = {
      HMAC: "bg-blue-500/20 text-blue-400",
      mTLS: "bg-purple-500/20 text-purple-400",
      OAuth2: "bg-orange-500/20 text-orange-400"
    };
    return (
      <span className={`px-2 py-0.5 rounded text-xs ${colors[method] || colors.HMAC}`}>
        {method}
      </span>
    );
  };

  return (
    <div className="space-y-6" data-testid="retail-stores-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-heading font-bold">Retail Stores</h1>
          <p className="text-muted-foreground">Non-bank voucher issuers (fee configurable per partner)</p>
        </div>
        <Dialog open={isDialogOpen} onOpenChange={(open) => { setIsDialogOpen(open); if (!open) resetForm(); }}>
          <DialogTrigger asChild>
            <Button data-testid="add-store-btn">
              <Plus className="h-4 w-4 mr-2" />
              Add Retail Store
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingStore ? "Edit Retail Store" : "Add Retail Store"}</DialogTitle>
              <DialogDescription>
                Configure retail store voucher issuer settings
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Basic Info */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Issuer Code</Label>
                  <Input
                    placeholder="e.g., SHOPRITE"
                    value={formData.issuer_code}
                    onChange={(e) => setFormData({ ...formData, issuer_code: e.target.value.toUpperCase() })}
                    disabled={!!editingStore}
                    required
                    data-testid="issuer-code-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Display Name</Label>
                  <Input
                    placeholder="e.g., Shoprite / Checkers / Usave"
                    value={formData.display_name}
                    onChange={(e) => setFormData({ ...formData, display_name: e.target.value })}
                    required
                    data-testid="display-name-input"
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label>Description</Label>
                <Input
                  placeholder="Brief description of the retailer"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                />
              </div>

              {/* Voucher Configuration */}
              <div className="border border-border rounded-lg p-4 space-y-4 bg-secondary/20">
                <div className="flex items-center gap-2 text-sm font-medium">
                  <Shield className="h-4 w-4 text-primary" />
                  Voucher Configuration
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Voucher Prefixes (comma-separated)</Label>
                    <Input
                      placeholder="e.g., SHP, CHK, USA"
                      value={formData.voucher_prefixes}
                      onChange={(e) => setFormData({ ...formData, voucher_prefixes: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Expiry Hours</Label>
                    <Input
                      type="number"
                      min="1"
                      max="168"
                      value={formData.voucher_expiry_hours}
                      onChange={(e) => setFormData({ ...formData, voucher_expiry_hours: parseInt(e.target.value) || 48 })}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label>PIN Min Length</Label>
                    <Select 
                      value={formData.pin_min_length.toString()} 
                      onValueChange={(v) => setFormData({ ...formData, pin_min_length: parseInt(v) })}
                    >
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="4">4 digits</SelectItem>
                        <SelectItem value="5">5 digits</SelectItem>
                        <SelectItem value="6">6 digits</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>PIN Max Length</Label>
                    <Select 
                      value={formData.pin_max_length.toString()} 
                      onValueChange={(v) => setFormData({ ...formData, pin_max_length: parseInt(v) })}
                    >
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="4">4 digits</SelectItem>
                        <SelectItem value="5">5 digits</SelectItem>
                        <SelectItem value="6">6 digits</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Auth Method</Label>
                    <Select 
                      value={formData.authentication_method} 
                      onValueChange={(v) => setFormData({ ...formData, authentication_method: v })}
                    >
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="HMAC">HMAC</SelectItem>
                        <SelectItem value="mTLS">mTLS</SelectItem>
                        <SelectItem value="OAuth2">OAuth2</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <Switch
                    checked={formData.partial_withdrawals_allowed}
                    onCheckedChange={(checked) => setFormData({ ...formData, partial_withdrawals_allowed: checked })}
                  />
                  <Label>Allow Partial Withdrawals</Label>
                </div>
              </div>

              {/* API Endpoints */}
              <div className="border border-border rounded-lg p-4 space-y-4 bg-secondary/20">
                <div className="text-sm font-medium">API Endpoints</div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>UAT Base URL</Label>
                    <Input
                      placeholder="https://uat-api.retailer.co.za"
                      value={formData.uat_base_url}
                      onChange={(e) => setFormData({ ...formData, uat_base_url: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Production Base URL</Label>
                    <Input
                      placeholder="https://api.retailer.co.za"
                      value={formData.production_base_url}
                      onChange={(e) => setFormData({ ...formData, production_base_url: e.target.value })}
                    />
                  </div>
                </div>
              </div>

              {/* Billing Config */}
              <div className="border border-border rounded-lg p-4 space-y-4 bg-secondary/20">
                <div className="flex items-center gap-2 text-sm font-medium">
                  <Mail className="h-4 w-4 text-primary" />
                  Billing Configuration
                </div>
                <div className="space-y-2">
                  <Label>Billing Address</Label>
                  <Input
                    placeholder="Physical address for invoices"
                    value={formData.billing_address}
                    onChange={(e) => setFormData({ ...formData, billing_address: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Billing Email To (comma-separated)</Label>
                  <Input
                    placeholder="billing@retailer.co.za"
                    value={formData.billing_email_to}
                    onChange={(e) => setFormData({ ...formData, billing_email_to: e.target.value })}
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Email CC</Label>
                    <Input
                      placeholder="cc@retailer.co.za"
                      value={formData.billing_email_cc}
                      onChange={(e) => setFormData({ ...formData, billing_email_cc: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Email BCC</Label>
                    <Input
                      placeholder="bcc@retailer.co.za"
                      value={formData.billing_email_bcc}
                      onChange={(e) => setFormData({ ...formData, billing_email_bcc: e.target.value })}
                    />
                  </div>
                </div>
              </div>

              <div className="flex justify-end gap-2 pt-4">
                <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" data-testid="save-store-btn">
                  {editingStore ? "Update Store" : "Create Store"}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="bg-card border-border">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-orange-500/20 rounded-lg">
                <Store className="h-5 w-5 text-orange-400" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Total Stores</p>
                <p className="text-xl font-mono font-bold">{stores.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-card border-border">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-500/20 rounded-lg">
                <DollarSign className="h-5 w-5 text-green-400" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Billing Rate</p>
                <p className="text-xl font-mono font-bold">Configurable</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-card border-border">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-500/20 rounded-lg">
                <Shield className="h-5 w-5 text-blue-400" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Active</p>
                <p className="text-xl font-mono font-bold">
                  {stores.filter(s => s.status === "ACTIVE").length}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-card border-border">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-500/20 rounded-lg">
                <Clock className="h-5 w-5 text-purple-400" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Total Transactions</p>
                <p className="text-xl font-mono font-bold">
                  {stores.reduce((sum, s) => sum + (s.transaction_count || 0), 0)}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Search by name or code..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-10"
          data-testid="search-stores"
        />
      </div>

      {/* Stores Table */}
      <Card className="bg-card border-border">
        <CardContent className="p-0">
          <Table className="data-table">
            <TableHeader>
              <TableRow>
                <TableHead>Code</TableHead>
                <TableHead>Name</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Auth</TableHead>
                <TableHead>PIN</TableHead>
                <TableHead>Expiry</TableHead>
                <TableHead>Rate</TableHead>
                <TableHead>Transactions</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={9} className="text-center py-8">
                    <div className="animate-pulse text-muted-foreground">Loading stores...</div>
                  </TableCell>
                </TableRow>
              ) : filteredStores.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} className="text-center py-8">
                    <div className="flex flex-col items-center gap-2">
                      <Store className="h-8 w-8 text-muted-foreground" />
                      <p className="text-muted-foreground">No retail stores found</p>
                    </div>
                  </TableCell>
                </TableRow>
              ) : (
                filteredStores.map((store) => (
                  <TableRow key={store.id}>
                    <TableCell>
                      <code className="font-mono text-sm bg-secondary px-2 py-1 rounded">
                        {store.issuer_code}
                      </code>
                    </TableCell>
                    <TableCell>
                      <div>
                        <span className="font-medium">{store.display_name}</span>
                        {store.voucher_prefixes?.length > 0 && (
                          <p className="text-xs text-muted-foreground">
                            Prefixes: {store.voucher_prefixes.join(", ")}
                          </p>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <StatusBadge status={store.status} />
                    </TableCell>
                    <TableCell>
                      <AuthMethodBadge method={store.authentication_method} />
                    </TableCell>
                    <TableCell className="font-mono text-sm">
                      {store.pin_min_length}-{store.pin_max_length}
                    </TableCell>
                    <TableCell className="text-sm">
                      {store.voucher_expiry_hours}h
                    </TableCell>
                    <TableCell>
                      <span className="font-mono text-orange-400">{formatCurrency(store.billing_rate)}</span>
                    </TableCell>
                    <TableCell className="font-mono">
                      {store.transaction_count || 0}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Switch
                          checked={store.status === "ACTIVE"}
                          onCheckedChange={() => handleToggleStatus(store)}
                          data-testid={`toggle-store-${store.issuer_code}`}
                        />
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleEdit(store)}
                          data-testid={`edit-store-${store.issuer_code}`}
                        >
                          <Edit2 className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-destructive hover:text-destructive"
                          onClick={() => handleDelete(store.issuer_code)}
                          data-testid={`delete-store-${store.issuer_code}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
};

export default RetailStores;
